import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { CalendarHoliday, WorkCalendarSummary } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { Popover } from '../../../../components/Popover';
import { Segmented } from '../../../../components/Segmented';
import { Select } from '../../../../components/Select';
import { anchorFrom, type AnchorRect } from '../../../../lib/anchored-panel';
import { formatIsoDate, formatNumber } from '../../../../lib/format';
import { isWorkingWeekday, monthsOf, shiftDay, yearChoices, yearTotals, type YearDay } from './calendar-year';
import type { HolidayActions } from './use-holiday-actions';
import styles from './YearCard.module.css';

const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
const ARROW_DAYS: Readonly<Record<string, number>> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
const NAME_BOX_W = 280;

interface YearCardProps {
  readonly calendar: WorkCalendarSummary;
  readonly year: number;
  readonly onYearChange: (year: number) => void;
  /** The years that have holidays, for the picker. */
  readonly holidayYears: readonly number[];
  readonly holidays: readonly CalendarHoliday[];
  /** False while another year's list is still on screen: a click then could act on the wrong days. */
  readonly isCurrent: boolean;
  readonly today: string;
  readonly canEdit: boolean;
  readonly actions: HolidayActions;
}

interface NameBox {
  readonly iso: string;
  readonly anchor: AnchorRect;
  /** What was typed, when the box opens again after the server refused it (review W20). */
  readonly name?: string;
}

/**
 * Calendars A, the middle column: the whole year. Days off are grey, holidays purple, and today has a
 * ring in the calendar's own zone. A click on a holiday removes it with Undo; a click on any other day
 * opens a small name box beside it, and Enter adds the holiday with Undo (owner, 2026-11-02).
 *
 * One day is in the tab order; the arrow keys move through the year (06-qol §2), so the 365 buttons
 * are one Tab stop.
 */
export function YearCard({ calendar, year, onYearChange, holidayYears, holidays, isCurrent, today, canEdit, actions }: YearCardProps): ReactNode {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState<NameBox | null>(null);
  const [focusedIso, setFocusedIso] = useState<string | null>(null);

  const byDate = new Map(holidays.filter((holiday) => holiday.holidayDate.startsWith(`${year}-`)).map((holiday) => [holiday.holidayDate, holiday]));
  const totals = yearTotals(year, calendar.workingDays, new Set(byDate.keys()));
  const thisYear = Number(today.slice(0, 4));
  const { recent, other } = yearChoices(thisYear, year, holidayYears);
  const firstOfYear = `${year}-01-01`;
  const tabIso = focusedIso?.startsWith(`${year}-`) ? focusedIso : today.startsWith(`${year}-`) ? today : firstOfYear;

  const focusDay = (iso: string): void => {
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-iso="${iso}"]`)?.focus();
  };

  const closeBox = (): void => {
    const iso = box?.iso;
    setBox(null);
    if (iso) focusDay(iso);
  };

  const onDay = (day: YearDay, element: HTMLElement): void => {
    setFocusedIso(day.iso);
    if (!canEdit || !isCurrent) return;
    const holiday = byDate.get(day.iso);
    if (holiday) {
      actions.remove(holiday);
      return;
    }
    setBox({ iso: day.iso, anchor: anchorFrom(element) });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const step = ARROW_DAYS[event.key];
    if (step === undefined || !(event.target instanceof HTMLElement) || !event.target.dataset.iso) return;
    const next = shiftDay(event.target.dataset.iso, step);
    if (!next.startsWith(`${year}-`)) return;
    event.preventDefault();
    setFocusedIso(next);
    focusDay(next);
  };

  return (
    <Card
      title={`${calendar.name} · ${year}`}
      action={
        <div className={styles.tools}>
          <ul className={styles.legend} aria-label="Key">
            <li>
              <i className={`${styles.swatch} ${styles.swatchOff}`} aria-hidden />
              Day off
            </li>
            <li>
              <i className={`${styles.swatch} ${styles.swatchHoliday}`} aria-hidden />
              Holiday
            </li>
            <li>
              <i className={`${styles.swatch} ${styles.swatchToday}`} aria-hidden />
              Today
            </li>
          </ul>
          <Segmented<string> label="Year" value={String(year)} options={recent.map((value) => ({ value: String(value), label: String(value) }))} onChange={(value) => onYearChange(Number(value))} />
          {other.length > 0 && (
            <span className={styles.earlier}>
              <Select label="Earlier years" placeholder="Earlier" value={recent.includes(year) ? null : String(year)} options={other.map((value) => ({ value: String(value), label: String(value) }))} onChange={(value) => onYearChange(Number(value))} />
            </span>
          )}
        </div>
      }
    >
      <div className={styles.body}>
        <div ref={gridRef} className={styles.months} onKeyDown={onKeyDown}>
          {monthsOf(year).map((month) => (
            <div key={month.index} className={styles.month} role="group" aria-label={`${month.name} ${year}`}>
              <span className={styles.monthName}>{month.name}</span>
              <div className={styles.grid}>
                {WEEKDAY_INITIALS.map((initial, index) => (
                  <span key={`head-${index}`} className={styles.weekday} aria-hidden>
                    {initial}
                  </span>
                ))}
                {Array.from({ length: month.leadingBlanks }, (_, index) => (
                  <span key={`blank-${index}`} aria-hidden />
                ))}
                {month.days.map((day) => {
                  const holiday = byDate.get(day.iso);
                  const isOff = !isWorkingWeekday(calendar.workingDays, day.weekday);
                  const isToday = day.iso === today;
                  return (
                    <button
                      key={day.iso}
                      type="button"
                      data-iso={day.iso}
                      className={styles.day}
                      data-off={isOff || undefined}
                      data-holiday={holiday ? true : undefined}
                      data-today={isToday || undefined}
                      aria-current={isToday ? 'date' : undefined}
                      aria-disabled={!canEdit || undefined}
                      tabIndex={day.iso === tabIso ? 0 : -1}
                      title={holiday?.name}
                      aria-label={dayLabel(day, holiday, isOff, canEdit)}
                      onClick={(event) => onDay(day, event.currentTarget)}
                    >
                      {day.day}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.totals}>
        <span className={styles.totalsText}>
          {formatNumber(totals.working)} working {totals.working === 1 ? 'day' : 'days'} · {formatNumber(totals.holidays)} {totals.holidays === 1 ? 'holiday' : 'holidays'} · {formatNumber(totals.daysOff)} {totals.daysOff === 1 ? 'day' : 'days'} off in {year}
        </span>
        {canEdit && <span className={styles.hint}>Click a date to add or remove a holiday</span>}
      </div>

      <Popover anchor={box?.anchor ?? null} onClose={closeBox} width={NAME_BOX_W} label="Name the holiday">
        {box && (
          <HolidayNameBox
            key={`${box.iso}:${box.name ?? ''}`}
            iso={box.iso}
            initialName={box.name ?? ''}
            onSave={(name) => {
              const { iso } = box;
              // A refusal opens the box again beside the same day with the name still typed; the toast says why.
              actions.add(iso, name, () => {
                const day = gridRef.current?.querySelector<HTMLButtonElement>(`[data-iso="${iso}"]`);
                if (day) setBox({ iso, anchor: anchorFrom(day), name });
              });
              closeBox();
            }}
          />
        )}
      </Popover>
    </Card>
  );
}

function dayLabel(day: YearDay, holiday: CalendarHoliday | undefined, isOff: boolean, canEdit: boolean): string {
  const date = `${WEEKDAY_NAMES[day.weekday] ?? ''} ${formatIsoDate(day.iso)}`;
  if (holiday) return `${date}, ${holiday.name}${canEdit ? '. Press to remove the holiday' : ''}`;
  return `${date}, ${isOff ? 'day off' : 'working day'}${canEdit ? '. Press to add a holiday' : ''}`;
}

/** The small box beside a clicked date: the name, and Enter saves. Esc closes it (the pop-up's own). */
function HolidayNameBox({ iso, initialName, onSave }: { readonly iso: string; readonly initialName: string; readonly onSave: (name: string) => void }): ReactNode {
  const [name, setName] = useState(initialName);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Review W4: the pop-up is measured and placed after it mounts, hidden until then, so `autoFocus` fired on a box that
  // couldn't take focus and typing went nowhere. Focus once it is placed, the frame after.
  useEffect(() => {
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);
  const save = (): void => {
    if (name.trim() !== '') onSave(name.trim());
  };
  return (
    <form
      className={styles.nameBox}
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <span className={styles.nameBoxDate}>Holiday on {formatIsoDate(iso)}</span>
      <Input ref={inputRef} aria-label="What it is" placeholder="What it is, e.g. Victory Day" maxLength={120} value={name} onChange={(event) => setName(event.target.value)} />
      <span className={styles.nameBoxActions}>
        <span className={styles.hint}>Enter adds it</span>
        <Button type="submit" size="sm" variant="solid" disabled={name.trim() === ''}>
          Add
        </Button>
      </span>
    </form>
  );
}
